import { ColliClient } from '@frontend/colli/api';
import { ColliStateBadge } from '@frontend/colli/common';
import { colliRequest } from '@frontend/colli/events';
import { Colli } from '@frontend/colli/types';
import { EntityType } from '@frontend/common';
import { DefaultBadge } from '@frontend/components/badge';
import { DefaultPrimaryButton } from '@frontend/components/common';
import { CommonMessage } from '@frontend/lang';
import { useModals } from '@frontend/modal';
import React from 'react';

import { PlatformDetailGrid } from '../../modal/platform-detail-grid/platform-detail-grid.component';
import { PlatformObjectBadge } from '../../modal/platform-object-badge/platform-object-badge.component';
import { ReactiveDetailRegistryEntry, ReactiveDetailView } from '../../modal/reactive-detail-shell/reactive-detail.types';
import { triggerRefetch } from '../../redux/detail-modal-slice';
import { useAppDispatch } from '../../redux/store';
import { ChangeColliStateModal } from './change-colli-state-modal/change-colli-state-modal.component';
import { ColliEventsSection, ColliGeneralSection, ColliHistorySection, ColliLabelsSection, ColliUsersSection } from './colli-detail-sections';

const ColliHeaderActionsView = ({ view }: { view: ReactiveDetailView<Colli, Colli> }) => {
    const { openModal } = useModals();
    const dispatch = useAppDispatch();
    const colli = view.extended ?? view.object;
    if (!colli) return null;

    return (
        <DefaultPrimaryButton
            text={CommonMessage.BUTTONS.CHANGE_STATUS}
            onClick={() =>
                openModal(
                    `change-colli-state-modal-${colli.id}`,
                    () => (
                        <ChangeColliStateModal
                            collis={[colli]}
                            onSubmitCallback={() => dispatch(triggerRefetch({ entityType: EntityType.COLLI, entityId: colli.id }))}
                        />
                    ),
                    {},
                    { className: 'w-[390px]' }
                )
            }
            size='sm'
        />
    );
};

const ColliDetailsCard = ({ colli }: { colli: Colli }) => (
    <div className='bg-background-surface outline-border-default inline-flex w-full flex-col items-start justify-start gap-6 self-stretch rounded-xl p-4 outline-1 outline-offset-[-1px]'>
        <div className='text-foreground-default justify-start text-xl font-semibold'>{CommonMessage.DETAIL.DETAILS}</div>
        <PlatformDetailGrid
            object={colli}
            cols={2}
            order={['label', 'type', 'state', 'notes', 'transaction_id', 'creation_timestamp', 'update_timestamp']}
            keyOverwrite={
                new Map([
                    ['label', CommonMessage.OBJECTS.PARCEL.DEFAULT],
                    ['type', CommonMessage.OBJECTS.COMMON_FIELDS.TYPE],
                    ['state', CommonMessage.OBJECTS.COMMON_FIELDS.STATE],
                    ['transaction_id', CommonMessage.OBJECTS.TRANSACTION.DEFAULT],
                    ['creation_timestamp', CommonMessage.OBJECTS.COMMON_FIELDS.CREATED_AT],
                    ['update_timestamp', CommonMessage.OBJECTS.COMMON_FIELDS.UPDATED_AT]
                ])
            }
            valueOverwrite={
                new Map([
                    [
                        'type',
                        (obj: Colli) => (
                            <DefaultBadge
                                styles={{ container: { color: 'bg-brand-muted' } }}
                                label={obj.type ? (obj.type.charAt(0).toUpperCase() + obj.type.slice(1)).replace(/_/g, ' ') : '/'}
                            />
                        )
                    ],
                    ['state', (obj: Colli) => <ColliStateBadge state={obj.state} />],
                    [
                        'transaction_id',
                        (obj: Colli) => (
                            <PlatformObjectBadge
                                id={obj.transaction_id}
                                entityType={EntityType.TRANSACTION}
                            />
                        )
                    ]
                ])
            }
        />
    </div>
);

const ColliReactiveTab = ({ view, tab }: { view: ReactiveDetailView<Colli, Colli>; tab: 'general' | 'labels' | 'users' | 'history' | 'events' }) => {
    const colli = view.extended ?? view.object;
    if (!colli) return null;
    if (tab === 'general') {
        return (
            <>
                <ColliDetailsCard colli={colli} />
                <ColliGeneralSection colli={colli} />
            </>
        );
    }
    if (tab === 'labels') return <ColliLabelsSection colli={colli} />;
    if (tab === 'users') return <ColliUsersSection colli={colli} />;
    if (tab === 'history') return <ColliHistorySection colli={colli} />;
    return <ColliEventsSection colli={colli} />;
};

export const colliReactiveDetail: ReactiveDetailRegistryEntry<Colli, Colli> = {
    entityType: EntityType.COLLI,
    resolveFunction: colliRequest,
    fetchExtended: ({ object, parents }) =>
        ColliClient.fetchAccountTransactionColli(object.account_id, parents.transaction_id ?? object.transaction_id, object.id),
    title: ({ extended, object }) => (
        <>
            {CommonMessage.OBJECTS.PARCEL.DEFAULT}: {extended?.label ?? object.label ?? object.id}
        </>
    ),
    headerActions: (view) => <ColliHeaderActionsView view={view} />,
    tabs: [
        {
            id: 'general',
            label: CommonMessage.DETAIL.GENERAL,
            render: (view) => (
                <ColliReactiveTab
                    view={view}
                    tab='general'
                />
            )
        },
        {
            id: 'labels',
            label: CommonMessage.OBJECTS.LABEL.PLURAL,
            render: (view) => (
                <ColliReactiveTab
                    view={view}
                    tab='labels'
                />
            )
        },
        {
            id: 'users',
            label: CommonMessage.OBJECTS.USER.PLURAL,
            render: (view) => (
                <ColliReactiveTab
                    view={view}
                    tab='users'
                />
            )
        },
        {
            id: 'history',
            label: CommonMessage.DETAIL.HISTORY,
            render: (view) => (
                <ColliReactiveTab
                    view={view}
                    tab='history'
                />
            )
        },
        {
            id: 'events',
            label: CommonMessage.DETAIL.EVENTS,
            render: (view) => (
                <ColliReactiveTab
                    view={view}
                    tab='events'
                />
            )
        }
    ]
};
